import * as React from 'react';
import { FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Parser } from 'json2csv';
import {
  ColumnDefinition,
  FunctionAccessorColumnDefinition,
  isFunctionAccessorBasedColumnDefinition,
  isPathAccessorBasedColumnDefinition,
  PathAccessorColumnDefinition
} from '../../helpers/table/table';
import propz from 'propz';

interface Props {
  selectedItems: any[];
  columns: ColumnDefinition[];
  label?: string;
  icon?: any;
  customClass?: string;
}

export const CsvIcon: FunctionComponent<Props> = props => {
  const { selectedItems, columns, label = 'Download as csv', icon = 'file-csv', customClass = '' } = props;

  const onClick = (): void => {
    const options = {
      user: {
        activeSchool: {
          ageGroupsNaming: 'ENGLISH'
        }
      }
    };
    const selectedItemsConverted = selectedItems.map(item => {
      let itemObj = {};
      columns.forEach(column => {
        let value;

        switch (true) {
          case isPathAccessorBasedColumnDefinition(column): {
            const pathAccessorBasedColumn = column as PathAccessorColumnDefinition;
            value = propz.get<any>(item, pathAccessorBasedColumn.accessor, '');
            break;
          }
          case isFunctionAccessorBasedColumnDefinition(column): {
            const funcAccessorColumn = column as FunctionAccessorColumnDefinition;
            value = funcAccessorColumn.accessor(item, options);
            break;
          }
        }
        const regExp = /\n/gi;
        const strValue = String(value)
          .trim()
          .replace(regExp, ' ');
        itemObj = { ...itemObj, ...{ [column.text]: strValue } };
      });

      return itemObj;
    });

    const fields = columns.map(column => column.text);
    const opts = { fields, delimiter: ',', eol: '\r\n' };

    try {
      const parser = new (Parser as any)(opts);
      const csv = parser.parse(selectedItemsConverted);
      // UTF-8 BOM preserves names with accents; sep= makes desktop Excel use
      // real columns even when the machine's regional list separator differs.
      const excelCsv = `\uFEFFsep=,\r\n${csv}`;

      // copy-paste from old frontend
      /*global Blob:true*/
      const blob = new Blob([excelCsv], { type: 'text/csv;charset=utf-8;' });
      const navigatorWithLegacySave = window.navigator as Navigator & { msSaveBlob?: (blob: Blob, name: string) => void };
      if (navigatorWithLegacySave.msSaveBlob) {
        // IE 10+
        navigatorWithLegacySave.msSaveBlob(blob, 'file.csv');
      } else {
        const link = document.createElement('a');
        if (link.download !== undefined) {
          // feature detection
          // Browsers that support HTML5 download attribute
          /*global URL:true*/
          const url = URL.createObjectURL(blob);
          link.setAttribute('href', url);
          link.setAttribute('download', 'file.csv');
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const isSelectedItemsExist = selectedItems.length > 0;
  const buttonClassName = isSelectedItemsExist ? 'btn-outline-primary' : 'btn-outline-secondary';

  return (
    <div className={`d-inline-block ${customClass}`}>
      <button className={`btn btn-sm ${buttonClassName}`} onClick={onClick} disabled={!isSelectedItemsExist}>
        <FontAwesomeIcon icon={icon} className="mr-2" />
        <span>{label}</span>
      </button>
    </div>
  );
};

CsvIcon.displayName = 'CsvIcon';
